import React, { useState, useRef, useEffect } from 'react';
import './InterviewerSelector.css';

const INTERVIEWER_CATEGORIES = {
  'Topics': [
    'questions', // This will be displayed as 'General'
    'art_history',
    'business',
    'celebrity',
    'film',
    'gaming',
    'historical_figures',
    'history',
    'hobbies',
    'internet_intellects',
    'literature',
    'live_players',
    'music',
    'philosophy',
    'politics',
    'pop_cultures',
    'society_culture_family',
    'spirituality_religion',
    'sports_athletes',
    // 'sports_history',
    'sports',
    'technology',
    'travel'
  ],
  'Dilemmas': [
    'aesthetics_dilemma',
    'aspirations_dilemma',
    'culture_social_identity_dilemma',
    'emotional_intellgience_dilemma',
    'epistemology_dilemma',
    'existentialism_dilemma',
    'politics_dilemma',
    'spirituality_religion_dilemma',
    'value_ethics_dilemma'
  ]
};

// Special display names mapping
const DISPLAY_NAME_MAPPING = {
  'questions': 'General'
};

// Get all interviewer keys
const getAllInterviewers = () => {
  return Object.values(INTERVIEWER_CATEGORIES).flat();
};

const formatInterviewerName = (name) => {
  // First check if we have a special mapping for this name
  if (DISPLAY_NAME_MAPPING[name]) {
    return DISPLAY_NAME_MAPPING[name];
  }
  
  // Otherwise use the default formatting
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace('Dilemma', '');
};

function InterviewerSelector({ value, onChange, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const [localSelections, setLocalSelections] = useState(Array.isArray(value) ? [...value] : []);
  const dropdownRef = useRef(null);
  const selectorRef = useRef(null);

  // Initialize local selections when value prop changes (only when dropdown is closed)
  useEffect(() => {
    if (!isOpen) {
      setLocalSelections(Array.isArray(value) ? [...value] : []);
    }
  }, [value, isOpen]);

  // Initialize with all interviewers if value is 'all'
  useEffect(() => {
    if (value === 'all') {
      onChange(getAllInterviewers());
    }
  }, []);

  // Handle clicks outside to close the dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      // If we're clicking inside either the selector or dropdown, don't close
      if (selectorRef.current && selectorRef.current.contains(event.target)) {
        return;
      }
      
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
        return;
      }
      
      // Apply selections to parent before closing
      if (JSON.stringify(localSelections) !== JSON.stringify(value)) {
        onChange(localSelections);
      }
      
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, localSelections, value, onChange]);

  const handleSelect = (interviewer) => {
    setLocalSelections(prev => {
      if (prev.includes(interviewer)) {
        return prev.filter(v => v !== interviewer);
      } else {
        return [...prev, interviewer];
      }
    });
  };

  const handleCategorySelect = (category) => {
    const categoryInterviewers = INTERVIEWER_CATEGORIES[category];
    
    setLocalSelections(prev => {
      const allSelected = categoryInterviewers.every(interviewer => 
        prev.includes(interviewer)
      );
      
      if (allSelected) {
        // Remove all from this category
        return prev.filter(v => !categoryInterviewers.includes(v));
      } else {
        // Add all from this category
        return [...new Set([...prev, ...categoryInterviewers])];
      }
    });
  };

  const toggleDropdown = (e) => {
    // Only toggle if clicking the button, not the dropdown content
    if (e.target.closest('.dropdown-menu')) {
      return;
    }
    
    if (!disabled) {
      if (isOpen) {
        // When closing, apply the selections to parent
        if (JSON.stringify(localSelections) !== JSON.stringify(value)) {
          onChange(localSelections);
        }
        setIsOpen(false);
      } else {
        setIsOpen(true);
      }
    }
  };

  const isCategorySelected = (category) => {
    return INTERVIEWER_CATEGORIES[category].every(interviewer => 
      localSelections.includes(interviewer)
    );
  };

  const getCategorySelectionText = (category) => {
    const isSelected = isCategorySelected(category);
    return isSelected ? 'Deselect All' : 'Select All';
  };

  const getDisplayValue = () => {
    if (localSelections.length === 0) {
      return 'Select at least 1 Topic';
    }
    if (localSelections.length === getAllInterviewers().length) {
      return 'Entertaining All Topics';
    }
    return `Entertaining ${localSelections.length} Topic${localSelections.length > 1 ? 's' : ''}`;
  };

  return (
    <div className="interviewer-selector" ref={selectorRef}>
      <button
        className="selector-button"
        onClick={toggleDropdown}
        disabled={disabled}
      >
        {getDisplayValue()}
        <span className="arrow">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="dropdown-menu" ref={dropdownRef}>
          {Object.entries(INTERVIEWER_CATEGORIES).map(([category, interviewers]) => (
            <div key={category} className="category-section">
              <div className="category-header">
                <span>{category}</span>
                <label className="category-checkbox">
                  <input
                    type="checkbox"
                    checked={isCategorySelected(category)}
                    onChange={() => handleCategorySelect(category)}
                  />
                  {getCategorySelectionText(category)}
                </label>
              </div>
              {interviewers.map(interviewer => (
                <div key={interviewer} className="menu-item">
                  <label className="interviewer-checkbox">
                    <input
                      type="checkbox"
                      checked={localSelections.includes(interviewer)}
                      onChange={() => handleSelect(interviewer)}
                    />
                    <span>{formatInterviewerName(interviewer)}</span>
                  </label>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default InterviewerSelector; 